import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { create } from "zustand";
import { apiInventoryGet } from "../components/Profile/api/apiInventoryGet";
import { InventorySchemaItems } from "../components/Profile/type/TInventory";
import { useAuthStore } from "./auth";

export const COMMODITY_KEYS = Object.values(InventorySchemaItems.keyof().Values);
export type TCommodityKey = (typeof COMMODITY_KEYS)[number];

interface CommoditiesStore {
  commoditiesBalance: Record<TCommodityKey, number>;
  increaseCommodityBalance: (comm: TCommodityKey, amount: number) => void;
}

const useCommoditiesBaseStore = create<CommoditiesStore>((set) => ({
  commoditiesBalance: {} as Record<TCommodityKey, number>,

  increaseCommodityBalance: (comm, amount) =>
    set((state) => ({
      commoditiesBalance: {
        ...state.commoditiesBalance,
        [comm]: (state.commoditiesBalance[comm] ?? 0) + amount,
      },
    })),
}));

export const useCommoditiesStore = () => {
  const { isAuthenticated } = useAuthStore();
  const setBalance = useCommoditiesBaseStore.setState;

  const {
    data: ownedItems,
    isSuccess: isInventoryLoaded,
    isLoading: isInventoryLoading,
  } = useQuery({
    queryKey: ["apiInventoryGet"],
    queryFn: apiInventoryGet,
    placeholderData: keepPreviousData,
    retry: 2,
    retryDelay: 300,
    enabled: isAuthenticated,
    select: (_) => _.Data.OwnedItems || {},
  });

  useEffect(() => {
    if (isInventoryLoaded) {
      setBalance({
        commoditiesBalance: Object.fromEntries(
          Object.entries(ownedItems || {}).map(([key, value]) => [key, value.Count]),
        ) as Record<TCommodityKey, number>,
      });
    }
  }, [isInventoryLoaded, setBalance, ownedItems]);

  return {
    ...useCommoditiesBaseStore(),
    isInventoryLoading,
  };
};

export const useCommodityBalance = (key: TCommodityKey) =>
  useCommoditiesBaseStore((s) => s.commoditiesBalance[key] ?? 0);
